import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Search } from 'lucide-react';
import type { SearchHit } from '@nexus/store';
import { useStore } from '../db/context';
import { useUi } from '../state/ui';
import { Snippet } from './Snippet';
import { ModuleIcon } from './ModuleIcon';

const DEBOUNCE_MS = 120;

export function SearchView({ initial }: { initial: string }) {
  const { t } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const [query, setQuery] = useState(initial);
  const [result, setResult] = useState<{ query: string; hits: SearchHit[]; ms: number } | null>(
    null,
  );

  useEffect(() => {
    if (!query.trim()) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const t0 = performance.now();
      const hits = await store.search(query, 100);
      if (!cancelled) setResult({ query, hits, ms: Math.round(performance.now() - t0) });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, store]);

  const shown = query.trim() && result ? result : null;

  return (
    <section className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10">
      <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
        <ModuleIcon kind="search" size="lg" />
        {t('search.title')}
      </h1>
      <div className="mt-6 flex items-center gap-2 rounded-xl border border-border px-3 focus-within:border-accent">
        <Search className="size-4 text-fg-muted" />
        <input
          autoFocus
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          aria-label={t('search.title')}
          className="w-full bg-transparent py-2 outline-none"
        />
      </div>
      {shown && (
        <p className="mt-3 text-xs text-fg-muted" role="status">
          {t('search.count', { count: shown.hits.length, ms: shown.ms })}
        </p>
      )}
      <ul className="mt-3 flex flex-col gap-1">
        {shown?.hits.map((h) => (
          <li key={h.pageId}>
            <button
              type="button"
              onClick={(e) => open({ kind: 'page', pageId: h.pageId }, e.ctrlKey || e.metaKey)}
              className="w-full rounded-md px-2 py-2 text-left hover:bg-bg-hover"
            >
              <span className="flex items-center gap-1.5 font-medium">
                <FileText className="size-4 text-fg-muted" />
                {h.title || t('page.untitled')}
              </span>
              {h.snippet && (
                <span className="mt-0.5 line-clamp-2 block text-sm text-fg-muted">
                  <Snippet text={h.snippet} />
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
